import {
  Button,
  ButtonColorEnum,
  ButtonSizeEnum,
  Checkbox,
  ClockIcon,
  CloseIcon,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  FormField,
  FormHint,
  FormInput,
  FormLabel,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  Select,
  Typography,
} from '@acceso/design-system';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCatalogueSelectProps } from '@/components/CatalogueListbox/use-catalogue-select-props';
import { FooterFormActions } from '@/components/FooterFormActions/FooterFormActions';
import { InputArea } from '@/components/FormFields/InputArea';
import { InputDate } from '@/components/FormFields/InputDate';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { StateForm } from '@/components/FormFields/StateForm';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { FeaturedPeopleDrawerForm } from '@/features/featured-people/components/FeaturedPeopleDrawerForm';
import { usePeopleFormPicker } from '@/features/featured-people/hooks/use-people-form-picker';
import { TFeaturedPeopleForm } from '@/features/featured-people/types';
import { I18nextTranslationKeys } from '@/i18n/i18next';

import { useBroadcastScheduleProgramBodySegmentToolkitDeclarationFormState } from '../hooks/useBroadcastScheduleProgramBodySegmentToolkitDeclarationFormState';

export function BroadcastScheduleProgramBodySegmentToolkitDeclarationsForm() {
  const { declarationData, activeSegment, playerVideoCurrentTime, handleDateTimeSegment } =
    useBroadcastScheduleProgram();
  const { t } = useTranslation();

  const {
    form,
    isCreatingPeople,
    peopleDrawer,
    canCreatePeople,
    savePersonAsPeople,
    setSavePersonAsPeople,
    isSavingPeople,
    handleSubmitPeople,
    renderAdditionalButtons,
    handleClickExit,
    handleSubmit,
  } = useBroadcastScheduleProgramBodySegmentToolkitDeclarationFormState();

  const { picker, selectCreatedPerson } = usePeopleFormPicker({
    form,
    fallbackLabel: declarationData?.entity_label,
    syncLabel: false,
  });
  const peopleSelectProps = useCatalogueSelectProps(picker);

  const handleCreatePerson = async (data: TFeaturedPeopleForm) => {
    const person = await handleSubmitPeople(data);
    selectCreatedPerson(person);
    return person;
  };

  const [newPerson, setNewPerson] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClickExit();
      }

      if (event.ctrlKey && event.key === 'F2') {
        handleDateTimeSegment(form, 'start_datetime');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeSegment, playerVideoCurrentTime, form]);

  useEffect(() => {
    setNewPerson(declarationData?.entity_label ? true : false);
  }, []);

  return (
    <>
      <form>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between border-b-[1px] border-accent-disabled-10">
            <Typography
              fontSize={FontSizeEnum.Xl}
              color={FontColorEnum.Dark10}
              weight={FontWeightEnum.Medium}
            >
              {`${declarationData ? t('Editar declaración') : t('Crear nueva declaración')}`}
            </Typography>

            <IconButton
              size={IconButtonSizeEnum.LG}
              color={IconButtonColorEnum.NONE_DEFAULT}
              onClick={handleClickExit}
            >
              <CloseIcon size={IconSizeEnum.LG} />
            </IconButton>
          </div>
          <InputArea form={form} name="text" label={t('Descripción')} bigSize />
          <RowContainer>
            <FormField>
              <div className="flex w-full items-center justify-between">
                <FormLabel>{t('Persona')}</FormLabel>
                <Button
                  size={ButtonSizeEnum.XS}
                  color={ButtonColorEnum.DEFAULT_GHOST}
                  onClick={() => {
                    if (!newPerson) {
                      form.setValue('people_id', null);
                    } else {
                      form.setValue('entity_label', '');
                    }
                    setNewPerson(prev => !prev);
                    setSavePersonAsPeople(false);
                  }}
                >
                  <Typography
                    fontSize={FontSizeEnum.Sm}
                    color={FontColorEnum.Dark10}
                    weight={FontWeightEnum.Medium}
                  >
                    {newPerson ? t('Existente') : t('Nueva')}
                  </Typography>
                </Button>
              </div>
              <div className={`${newPerson ? 'block' : 'hidden'} w-full`}>
                <Controller
                  control={form.control}
                  name="entity_label"
                  render={({ field: { value, onChange }, fieldState: { error } }) => {
                    return (
                      <>
                        <FormInput
                          value={value}
                          onChange={onChange}
                          placeholder={t('Persona')}
                          hasError={!!error}
                        />
                        {error && (
                          <FormHint color={FontColorEnum.Error20} type="error">
                            {error.message}
                          </FormHint>
                        )}
                      </>
                    );
                  }}
                />
                {canCreatePeople && (
                  <Checkbox
                    labelClassName="ml-1"
                    className="mt-2"
                    checked={savePersonAsPeople}
                    disabled={isSavingPeople}
                    onChange={() => setSavePersonAsPeople(prev => !prev)}
                  >
                    <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.Dark10}>
                      {t('Guardar en Personas de Interés')}
                    </Typography>
                  </Checkbox>
                )}
              </div>
              <div className={`${newPerson ? 'hidden' : 'block'} w-full`}>
                <Controller
                  control={form.control}
                  name="people_id"
                  render={({ field: { value, onChange }, fieldState: { error } }) => {
                    return (
                      <>
                        <Select
                          {...peopleSelectProps}
                          value={value}
                          onChange={onChange}
                          hasError={!!error}
                          placeholder={t('Persona')}
                          additionalButtons={renderAdditionalButtons()}
                        />
                        {error && (
                          <FormHint color={FontColorEnum.Error20} type="error">
                            {error.message}
                          </FormHint>
                        )}
                      </>
                    );
                  }}
                />
              </div>
            </FormField>

            <InputDate
              form={form}
              name="start_datetime"
              label={t('Inicio')}
              seconds
              help
              tooltipHelp={t('Rellenar') as I18nextTranslationKeys}
              tooltipKeyword="F2"
              tooltipModifiers={['Ctrl']}
              onClickTooltip={() => handleDateTimeSegment(form, 'start_datetime')}
              iconTooltip={ClockIcon}
            />
          </RowContainer>

          <StateForm
            className="!justify-start"
            form={form}
            name="review_state"
            activedName={t('Marcar como revisado')}
            desactivedName={t('No revisado')}
          />

          <FooterFormActions
            form={form}
            handleCancel={handleClickExit}
            handleSubmit={handleSubmit}
            isLoading={isSavingPeople}
          />
        </div>
      </form>

      <FeaturedPeopleDrawerForm
        isOpen={peopleDrawer.isOpen}
        isMutating={isCreatingPeople}
        onClose={peopleDrawer.close}
        onSubmit={handleCreatePerson}
      />
    </>
  );
}
